import styled from 'styled-components';
import Link from 'next/link';
import snakeCase from 'lodash/snakeCase';

import { FeatureFlags } from 'featureFlags/constants';
import { HoverDropdownMenu } from 'shared/components/HoverDropdownMenu';
import { IFooterBreadcrumb, IFooterData } from 'layout/footer/types/FooterData';
import { OneTrustLink } from 'tracking/components/OneTrustLink';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { t } from 'localization/utils/t';
import { UnorderedList } from 'shared/components/UnorderedList';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useSitePreferencesModal } from 'shared/hooks/useSitePreferencesModal';
import { variableFontRegular } from 'styles/theme';

const TP = 'layout.footer.components.Footer';

const MenuItem = ({ label }: { label: string }) => (
  <MenuItem.LabelWrapper>{label}</MenuItem.LabelWrapper>
);

const DropdownMenu = ({
  menuItems,
  label,
}: {
  menuItems: Array<IFooterBreadcrumb>;
  label: string;
}) => {
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const handleBreadcrumbClick = (name: string) => {
    if (name === 'Gift Cards') {
      sendTrackingEvent('GC_FOOTER_LINK_TAP', {
        service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
      });
    } else {
      sendTrackingEvent('NAVIGATION_FOOTER_DRILLDOWN_TAP', {
        footer_title: snakeCase(label),
        footer_subcategory: snakeCase(name),
        service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
      });
    }
  };

  return (
    <DropdownMenu.Wrapper>
      {menuItems.map(({ name, slug, id, externalUrl, openInNewTab }) => (
        <DropdownMenu.LinkWrapper key={id}>
          <Link
            href={externalUrl || `/${slug}`}
            passHref
            tabIndex={0}
            role="link"
            target={openInNewTab ? '_blank' : '_self'}
            rel="noreferrer noopener"
            onClick={() => handleBreadcrumbClick(name)}
            onKeyDown={() => handleBreadcrumbClick(name)}
          >
            {name}
          </Link>
        </DropdownMenu.LinkWrapper>
      ))}
    </DropdownMenu.Wrapper>
  );
};

export const FooterNavigationDesktop = ({ footerLinks }: { footerLinks: Array<IFooterData> }) => {
  const isOneTrustEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_ONE_TRUST);
  const { openSitePreferencesModal } = useSitePreferencesModal();

  return (
    <FooterNavigationDesktop.Wrapper>
      {footerLinks.map(({ title, breadcrumbs, id, slug }) => {
        if (slug) {
          return (
            <FooterNavigationDesktop.NavLink key={id}>
              <Link
                href={`/${slug}`}
                passHref
                prefetch={false}
                target="_blank"
                rel="noreferrer noopener"
                data-qa={`footer_${slug}`}
              >
                {title}
              </Link>
            </FooterNavigationDesktop.NavLink>
          );
        }

        return (
          <HoverDropdownMenu
            dropdownMenu={breadcrumbs && <DropdownMenu label={title} menuItems={breadcrumbs} />}
            menuItem={<MenuItem label={title} />}
            style={{ marginRight: '16px' }}
            align="left"
            key={id}
          />
        );
      })}

      <FooterNavigationDesktop.StaticLinks>
        <FooterNavigationDesktop.NavLink data-qa="footer_terms">
          <Link href="/terms" passHref prefetch={false} target="_blank" rel="noreferrer noopener">
            {t(`${TP}.terms`, 'Terms')}
          </Link>
        </FooterNavigationDesktop.NavLink>
        <FooterNavigationDesktop.NavLink data-qa="footer_privacy">
          <Link href="/privacy" passHref prefetch={false} target="_blank" rel="noreferrer noopener">
            {t(`${TP}.privacy`, 'Privacy')}
          </Link>
        </FooterNavigationDesktop.NavLink>
        {isOneTrustEnabled && (
          <FooterNavigationDesktop.NavLink data-qa="footer_cookies">
            <OneTrustLink />
          </FooterNavigationDesktop.NavLink>
        )}
        <FooterNavigationDesktop.NavLink data-qa="footer_site_preferences">
          <button type="button" onClick={openSitePreferencesModal}>
            {t(`${TP}.locationPreferences`, 'Location Preferences')}
          </button>
        </FooterNavigationDesktop.NavLink>
        <FooterNavigationDesktop.NavLink data-qa="footer_accessibility">
          <Link href="/accessibility" passHref prefetch={false}>
            {t(`${TP}.accessibility`, 'Accessibility')}
          </Link>
        </FooterNavigationDesktop.NavLink>
        <FooterNavigationDesktop.NavLink data-qa="footer_sitemap">
          <Link href="/sitemap-html" passHref prefetch={false}>
            {t(`${TP}.sitemap`, 'Sitemap')}
          </Link>
        </FooterNavigationDesktop.NavLink>
      </FooterNavigationDesktop.StaticLinks>
    </FooterNavigationDesktop.Wrapper>
  );
};

FooterNavigationDesktop.Wrapper = styled(UnorderedList)`
  display: flex;

  ${variableFontRegular({ size: '12.5px', height: '14px' })};
`;

MenuItem.LabelWrapper = styled.div`
  display: flex;
  cursor: default;

  &:active,
  &:focus {
    outline: none;
  }
`;

DropdownMenu.Wrapper = styled(UnorderedList)`
  display: flex;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
`;

DropdownMenu.LinkWrapper = styled.li`
  color: ${({ theme }) => theme.colors.darkGray};
  white-space: nowrap;
  padding: 12px 0 20px 0;

  > a {
    color: ${({ theme }) => theme.navigation.textColor};
    padding: 12px 16px 20px 0;
  }

  &:last-child {
    margin-right: 0;
  }

  a:hover {
    color: ${({ theme }) => theme.navigation.textColor};
    text-decoration: underline;
  }
`;

FooterNavigationDesktop.NavLink = styled.li`
  margin-right: 16px;
  cursor: pointer;
  white-space: nowrap;

  > button {
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }

  &:hover {
    text-decoration: underline;
  }
`;

FooterNavigationDesktop.StaticLinks = styled(UnorderedList)`
  display: flex;
  flex-wrap: wrap;
  padding: 9px 20px 0;
  row-gap: 18px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 0;
  }
`;

FooterNavigationDesktop.NavLink = styled.li`
  margin-right: 16px;
  cursor: pointer;

  > button {
    padding: 0;

    :hover {
      text-decoration: underline;
    }
  }

  :hover {
    text-decoration: underline;
  }
`;
